import * as Y from "yjs";
import RBush from "rbush";
import {nanoid} from "nanoid";
import {WebsocketProvider} from "y-websocket";
import {useCallback, useEffect, useRef, useState, useSyncExternalStore} from "react";
import {VisionBoardId} from "../../cdx-models/VisionBoard";
import cdxEnv from "../../env";
import {
  NodeSide,
  VisionBoardEdge,
  VisionBoardEdgeId,
  VisionBoardNode,
  VisionBoardNodeId,
} from "../../cdx-models/utils/extended-fields";
import {MiniEvent} from "@cdx/common";
import shallowEqual from "shallowequal";
import throttle from "lodash/throttle";
import {
  AwarenessStore,
  BoundingBoxStore,
  BushRect,
  CursorEvent,
  getAnchorPos,
  SelectionEvent,
  TwoNums,
} from "./canvas-utils";
import {UserId} from "../../cdx-models/User";

const createId = () => nanoid(12);

type NodeAndSide = {node: NodeBushEntry; side: NodeSide};
export type NodeAndSideAnchorPos = NodeAndSide & {anchor: [number, number]};

export type EdgeBushEntry = {
  id: VisionBoardEdgeId;
  edge: Y.Map<any>;
  type: "edge";
  source: NodeAndSideAnchorPos;
  target: NodeAndSideAnchorPos;
} & BushRect;

export type NodeBushEntry = {
  id: VisionBoardNodeId;
  node: Y.Map<any>;
  type: "node";
} & BushRect;

export type BushEntry = EdgeBushEntry | NodeBushEntry;

const nodeToBushEntry = (id: VisionBoardNodeId, node: Y.Map<any>): NodeBushEntry => {
  const pos = node.get("pos") as VisionBoardNode["pos"];
  return {
    minX: pos.x,
    minY: pos.y,
    maxX: pos.x + pos.w,
    maxY: pos.y + pos.h,
    id,
    node,
    type: "node",
  };
};

const addToEdgeMap = (
  edgeMap: EdgeMap,
  sourceId: VisionBoardNodeId,
  targetId: VisionBoardNodeId,
  edgeEntry: EdgeBushEntry
) => {
  const addEntry = (id: VisionBoardNodeId, otherId: VisionBoardNodeId, entry: EdgeBushEntry) => {
    const exist = edgeMap.get(id);
    if (exist) {
      const existList = exist.get(otherId);
      if (existList) {
        existList.push(entry);
      } else {
        exist.set(otherId, [entry]);
      }
    } else {
      edgeMap.set(id, new Map([[otherId, [entry]]]));
    }
  };
  addEntry(sourceId, targetId, edgeEntry);
  addEntry(targetId, sourceId, edgeEntry);
};

const getEdgeDimensions = ([sx, sy]: [number, number], [tx, ty]: [number, number]) => {
  const [minX, maxX] = sx < tx ? [sx, tx] : [tx, sx];
  const [minY, maxY] = sy < ty ? [sy, ty] : [ty, sy];
  return {minX, minY, maxX, maxY};
};

const edgeToBushEntry = (
  id: VisionBoardEdgeId,
  edge: Y.Map<any>,
  nodesById: Map<string, NodeBushEntry>,
  edgeMap: EdgeMap
): EdgeBushEntry | null => {
  const {source, target} = edge.get("value") as VisionBoardEdge["value"];
  const sourceNode = nodesById.get(source.nodeId);
  const targetNode = nodesById.get(target.nodeId);
  if (!sourceNode || !targetNode) {
    console.warn("Edge", id, "references non-existing node", source.nodeId, target.nodeId);
    return null;
  }

  const sourcePos = getAnchorPos({node: sourceNode, side: source.side});
  const targetPos = getAnchorPos({node: targetNode, side: target.side});

  const entry: EdgeBushEntry = {
    type: "edge",
    id,
    edge,
    source: {node: sourceNode, side: source.side, anchor: sourcePos},
    target: {node: targetNode, side: target.side, anchor: targetPos},
    ...getEdgeDimensions(sourcePos, targetPos),
  };
  addToEdgeMap(edgeMap, sourceNode.id, targetNode.id, entry);
  return entry;
};

export type NodeMoveArg =
  | {type: "rect"; value: BushRect}
  | {type: "move"; value: [number, number]; initial: BushRect};
type NodeMoveListenerCb = (movedNodes: Map<VisionBoardNodeId, NodeMoveArg>) => void;

export type TreeStore = {
  useEntries: (box: BoundingBoxStore) => BushEntry[];
  getNodesSnapshot: (rect: BushRect | null) => NodeBushEntry[];
  getEdgesSnaphot: (rect: BushRect | null) => EdgeBushEntry[];
  unsubscribe: () => void;
  addNode: (node: VisionBoardNode) => {node: Y.Map<unknown>; id: VisionBoardNodeId};
  getNode: (id: VisionBoardNodeId) => NodeBushEntry | undefined;
  addEdge: (edgeValue: VisionBoardEdge["value"]) => Y.Map<unknown>;
  registerEdgeListener: (id: VisionBoardEdgeId, cb: () => void) => () => void;
  registerNodeMoveListener: <T extends VisionBoardNodeId[]>(
    ids: T,
    cb: NodeMoveListenerCb
  ) => () => void;
  emitNodeMoveListener: (id: VisionBoardNodeId, r: NodeMoveArg) => void;
  undoManager: Y.UndoManager;
  doc: Y.Doc;
  registerAnyUpdateListener: (cb: () => void) => () => void;
};

type EdgeMap = Map<VisionBoardNodeId, Map<VisionBoardNodeId, EdgeBushEntry[]>>;

const setupNodeObservers = (
  nodeMap: Y.Map<any>,
  nodesTree: RBush<NodeBushEntry>,
  nodesById: Map<VisionBoardNodeId, NodeBushEntry>,
  edges: EdgeMap,
  edgesTree: RBush<EdgeBushEntry>,
  onUpdate: () => void,
  onEdgeUpdated: (id: VisionBoardEdgeId) => void
) => {
  const handleObserveNodes = ({changes}: Y.YMapEvent<Y.Map<any>>) => {
    let modified = false;
    for (const [rawId, change] of changes.keys) {
      const id = rawId as VisionBoardNodeId;
      switch (change.action) {
        case "add":
          const entry = nodeToBushEntry(id, nodeMap.get(id) as Y.Map<any>);
          nodesById.set(id, entry);
          nodesTree.insert(entry);
          modified = true;
          break;
        case "update":
          break;
        case "delete":
          nodesTree.remove(nodesById.get(id)!);
          nodesById.delete(id);
          const nodeEdges = edges.get(id);
          if (nodeEdges) {
            for (const edgeEntries of nodeEdges.values()) {
              for (const edgeEntry of edgeEntries) {
                const parent = edgeEntry.edge.parent;
                if (parent) (parent as Y.Map<any>).delete(edgeEntry.id);
              }
            }
          }
          modified = true;
          break;
      }
    }
    if (modified) onUpdate();
  };
  nodeMap.observe(handleObserveNodes);
  const handleDeepObserveNodes = (events: Array<any>) => {
    let modified = false;
    for (const ev of events) {
      if (ev.keysChanged.has("pos")) {
        const id = ev.target._item.parentSub;
        const entry = nodesById.get(id);
        if (!entry) continue;
        nodesTree.remove(entry);
        const pos = entry.node.get("pos") as VisionBoardNode["pos"];
        entry.minX = pos.x;
        entry.minY = pos.y;
        entry.maxX = pos.x + pos.w;
        entry.maxY = pos.y + pos.h;
        nodesTree.insert(entry);
        const nodeEdges = edges.get(id);
        if (nodeEdges) {
          for (const edgeEntries of nodeEdges.values()) {
            for (const edgeEntry of edgeEntries) {
              const sourcePos = getAnchorPos(edgeEntry.source);
              const targetPos = getAnchorPos(edgeEntry.target);
              const dims = getEdgeDimensions(sourcePos, targetPos);
              edgesTree.remove(edgeEntry);
              edgeEntry.minX = dims.minX;
              edgeEntry.minY = dims.minY;
              edgeEntry.maxX = dims.maxX;
              edgeEntry.maxY = dims.maxY;
              edgeEntry.source.anchor = sourcePos;
              edgeEntry.target.anchor = targetPos;
              edgesTree.insert(edgeEntry);
              onEdgeUpdated(edgeEntry.id);
            }
          }
        }
        modified = true;
      }
    }
    if (modified) onUpdate();
  };
  nodeMap.observeDeep(handleDeepObserveNodes);
  return () => {
    nodeMap.unobserve(handleObserveNodes);
    nodeMap.unobserveDeep(handleDeepObserveNodes);
  };
};

const setupEdgeObservers = (
  edgeMap: Y.Map<any>,
  edgesTree: RBush<EdgeBushEntry>,
  nodesById: Map<VisionBoardNodeId, NodeBushEntry>,
  edges: EdgeMap,
  edgeEntries: Map<VisionBoardEdgeId, EdgeBushEntry>,
  onUpdate: () => void
) => {
  const removeFromEdgeMap = (
    entry: EdgeBushEntry,
    firstId: VisionBoardNodeId,
    otherId: VisionBoardNodeId
  ) => {
    const entries = edges.get(firstId);
    if (!entries) return;
    const list = entries.get(otherId);
    if (list) {
      const idx = list.indexOf(entry);
      if (idx !== -1) list.splice(idx, 1);
      if (list.length === 0) entries.delete(otherId);
    }
    if (entries.size === 0) edges.delete(firstId);
  };

  const handleObserveEdges = ({changes}: Y.YMapEvent<any>) => {
    let modified = false;
    for (const [rawId, change] of changes.keys) {
      const id = rawId as VisionBoardEdgeId;
      switch (change.action) {
        case "add": {
          const entry = edgeToBushEntry(id, edgeMap.get(id)! as Y.Map<any>, nodesById, edges);
          if (!entry) {
            console.warn("Failed to build edge entry for", id);
            break;
          }
          edgesTree.insert(entry);
          edgeEntries.set(id, entry);
          modified = true;
          break;
        }
        case "update":
          break;
        case "delete": {
          const entry = edgeEntries.get(id)!;
          const {source, target} = entry;
          removeFromEdgeMap(entry, source.node.id, target.node.id);
          removeFromEdgeMap(entry, target.node.id, source.node.id);
          edgesTree.remove(entry);
          edgeEntries.delete(id);
          modified = true;
          break;
        }
      }
    }
    if (modified) onUpdate();
  };
  edgeMap.observe(handleObserveEdges);

  const handleDeepObserveEdges = (events: Array<any>) => {
    let modified = false;
    for (const ev of events) {
      if (ev.keysChanged.has("value")) {
        const id = ev.target._item.parentSub;
        const prevEntry = edgeEntries.get(id);
        if (!prevEntry) continue;
        removeFromEdgeMap(prevEntry, prevEntry.source.node.id, prevEntry.target.node.id);
        removeFromEdgeMap(prevEntry, prevEntry.target.node.id, prevEntry.source.node.id);
        edgesTree.remove(prevEntry);
        edgeEntries.delete(id);
        modified = true;
        const entry = edgeToBushEntry(id, edgeMap.get(id)! as Y.Map<any>, nodesById, edges);
        if (!entry) {
          console.warn("Failed to build edge entry for", id);
          break;
        }
        edgesTree.insert(entry);
        edgeEntries.set(id, entry);
      }
    }
    if (modified) onUpdate();
  };

  edgeMap.observeDeep(handleDeepObserveEdges);
  return () => {
    edgeMap.unobserve(handleObserveEdges);
    edgeMap.unobserveDeep(handleDeepObserveEdges);
  };
};

export const buildTreeStore = (doc: Y.Doc): TreeStore => {
  const nodesTree = new RBush<NodeBushEntry>();
  const nodeEntries: NodeBushEntry[] = [];
  const nodeMap = doc.getMap<Y.Map<any>>("nodes");
  const nodesById = new Map<VisionBoardNodeId, NodeBushEntry>();
  for (const [id, node] of nodeMap) {
    const entry = nodeToBushEntry(id as VisionBoardNodeId, node);
    nodeEntries.push(entry);
    nodesById.set(id as VisionBoardNodeId, entry);
  }
  nodesTree.load(nodeEntries);

  const edgesTree = new RBush<EdgeBushEntry>();
  const edgeEntries: EdgeBushEntry[] = [];
  const edgeMap = doc.getMap<Y.Map<any>>("edges");
  const edges: EdgeMap = new Map();
  const edgesById = new Map<VisionBoardEdgeId, EdgeBushEntry>();
  for (const [id, edge] of edgeMap) {
    const entry = edgeToBushEntry(id as VisionBoardEdgeId, edge, nodesById, edges);
    if (entry) {
      edgeEntries.push(entry);
      edgesById.set(id as VisionBoardEdgeId, entry);
    } else {
      edgeMap.delete(id);
    }
  }
  edgesTree.load(edgeEntries);

  const edgeListeners = new Map<VisionBoardEdgeId, Set<() => void>>();

  const registerEdgeListener = (id: VisionBoardEdgeId, cb: () => void) => {
    let cbSet = edgeListeners.get(id);
    if (cbSet) {
      cbSet.add(cb);
    } else {
      cbSet = new Set([cb]);
      edgeListeners.set(id, cbSet);
    }
    return () => {
      cbSet.delete(cb);
      if (cbSet.size === 0) edgeListeners.delete(id);
    };
  };
  const handleEdgeUpdate = (id: VisionBoardEdgeId) => {
    const cbSet = edgeListeners.get(id);
    if (cbSet) {
      cbSet.forEach((cb) => cb());
    }
  };

  const entryUpdateEvent = new MiniEvent<void>();
  let v = 0;
  const handleEntryUpdate = () => {
    v += 1;
    entryUpdateEvent.emit();
  };
  const unsubscribeNodeObservers = setupNodeObservers(
    nodeMap,
    nodesTree,
    nodesById,
    edges,
    edgesTree,
    handleEntryUpdate,
    handleEdgeUpdate
  );
  const unsubEdgeObsevers = setupEdgeObservers(
    edgeMap,
    edgesTree,
    nodesById,
    edges,
    edgesById,
    handleEntryUpdate
  );

  const moveListeners = new Map<VisionBoardNodeId, Set<NodeMoveListenerCb>>();
  let isQueued = false;
  const movedNodes = new Map<VisionBoardNodeId, NodeMoveArg>();
  const pendingCbs = new Set<NodeMoveListenerCb>();

  const emitBoxMoveListener: TreeStore["emitNodeMoveListener"] = (id, r) => {
    const cbs = moveListeners.get(id);
    if (!cbs) return;
    cbs.forEach((cb) => pendingCbs.add(cb));
    if (!isQueued) {
      queueMicrotask(() => {
        pendingCbs.forEach((cb) => cb(movedNodes));
        isQueued = false;
        movedNodes.clear();
        pendingCbs.clear();
      });
      isQueued = true;
    }
    movedNodes.set(id, r);
  };

  const registerBoxMoveListener: TreeStore["registerNodeMoveListener"] = (ids, cb) => {
    const cbSets: [VisionBoardNodeId, Set<NodeMoveListenerCb>][] = [];
    for (const id of ids) {
      let cbSet = moveListeners.get(id);
      if (!cbSet) {
        cbSet = new Set();
        moveListeners.set(id, cbSet);
      }
      cbSet.add(cb);
      cbSets.push([id, cbSet]);
    }
    return () => {
      for (const [id, cbSet] of cbSets) {
        cbSet.delete(cb);
        if (cbSet.size === 0) moveListeners.delete(id);
      }
    };
  };

  let anyListener = new Set<() => void>();

  const registerAnyUpdateListener = (cb: () => void) => {
    anyListener.add(cb);
    return () => anyListener.delete(cb);
  };
  doc.on("update", () => {
    anyListener.forEach((cb) => cb());
  });

  return {
    useEntries(box) {
      const refs = useRef<null | [BushRect, number, BushEntry[]]>(null);
      const subscriber = useCallback(
        (cb: () => void) => {
          const unsubBox = box.subscribe(throttle(cb, 25));
          const unsubStore = entryUpdateEvent.addListener(cb);
          return () => {
            unsubBox();
            unsubStore();
          };
        },
        [box]
      );
      return useSyncExternalStore(subscriber, () => {
        const rect = box.getSnapshot();
        if (refs.current !== null) {
          const [prevRect, prevV, prevOut] = refs.current;
          if (prevV === v && shallowEqual(prevRect, rect)) return prevOut;
        }
        const out =
          rect.minX !== rect.maxX ? [...edgesTree.search(rect), ...nodesTree.search(rect)] : [];
        const maybePrevOut = refs.current?.[2] ?? null;
        if (maybePrevOut && shallowEqual(maybePrevOut, out)) {
          refs.current = [rect, v, maybePrevOut];
          return maybePrevOut;
        } else {
          refs.current = [rect, v, out];
          return out;
        }
      });
    },
    getNodesSnapshot: (rect: BushRect | null) => (rect ? nodesTree.search(rect) : nodesTree.all()),
    getEdgesSnaphot: (rect: BushRect | null) => (rect ? edgesTree.search(rect) : edgesTree.all()),
    unsubscribe: () => {
      unsubscribeNodeObservers();
      unsubEdgeObsevers();
      edgeListeners.clear();
    },
    addNode: ({type, value, pos}) => {
      return doc.transact(() => {
        const id = createId() as VisionBoardNodeId;
        const newNode = new Y.Map();
        newNode.set("type", type);
        newNode.set("value", value);
        newNode.set("pos", pos);
        nodeMap.set(id, newNode);
        return {node: newNode, id};
      });
    },
    addEdge: (edgeValue) => {
      return doc.transact(() => {
        const id = createId() as VisionBoardEdgeId;
        const newNode = new Y.Map();
        newNode.set("value", edgeValue);
        edgeMap.set(id, newNode);
        return newNode;
      });
    },
    registerEdgeListener,
    getNode: (id: VisionBoardNodeId) => nodesById.get(id),
    registerNodeMoveListener: registerBoxMoveListener,
    emitNodeMoveListener: emitBoxMoveListener,
    undoManager: new Y.UndoManager([nodeMap, edgeMap]),
    doc,
    registerAnyUpdateListener,
  };
};

const createAwarenesStore = (
  awareness: WebsocketProvider["awareness"],
  myClientId: number
): AwarenessStore => {
  const cursorSubs = new MiniEvent<CursorEvent>();
  const selectionSubs = new MiniEvent<SelectionEvent>();

  awareness.on("change", (ev: {added: number[]; updated: number[]; removed: number[]}) => {
    const cursors: CursorEvent = new Map();
    const selections: SelectionEvent = new Map();
    for (const [clientId, state] of awareness.getStates()) {
      if (clientId === myClientId) continue;
      if (state.cursor && state.cursor.pos) {
        cursors.set(clientId, state.cursor);
      }
      if (state.selection) {
        for (const id of state.selection.nodeIds) {
          const exist = selections.get(id);
          if (exist) {
            exist.add(state.selection.id);
          } else {
            selections.set(id, new Set([state.selection.id]));
          }
        }
      }
    }
    cursorSubs.emit(cursors);
    selectionSubs.emit(selections);
  });

  return {
    setCursor: (id: UserId, pos: TwoNums | null) => {
      awareness.setLocalStateField("cursor", {id, pos});
    },
    setSelectionIds: (id: UserId, nodeIds: VisionBoardNodeId[]) => {
      awareness.setLocalStateField("selection", {id, nodeIds});
    },
    myClientId,
    registerCursorListener: cursorSubs.addListener,
    registerSelectionListener: selectionSubs.addListener,
  };
};

const useSyncedVisionBoardDoc = (id: VisionBoardId | null) => {
  const [state, setState] = useState<{doc: Y.Doc; awarenessStore: AwarenessStore} | null>(null);
  useEffect(() => {
    setState(null);
    if (!id) return;

    const yDoc = new Y.Doc();
    const wsProvider = new WebsocketProvider(`${cdxEnv.ROOM_SYNC_HOST}/vision-board`, id, yDoc);
    const store = createAwarenesStore(wsProvider.awareness, yDoc.clientID);
    // wsProvider.on("status", (event) => {
    //   console.log(event);
    // });
    wsProvider.on("sync", (isSynced) => {
      if (isSynced) setState({doc: yDoc, awarenessStore: store});
    });
    wsProvider.on("connection-error", (error) => {
      console.error(error);
    });
    return () => {
      wsProvider.destroy();
    };
  }, [id]);
  return state;
};

export default useSyncedVisionBoardDoc;
